/**
 * IndexedDB wrapper for offline booking storage
 * Database: barbriq-offline
 * Stores:
 *   - pendingBookings (bookings waiting to sync)
 *   - cachedBarbers (barber data for offline display)
 *   - cachedServices (service data for offline display)
 *   - userPreferences (user's preferred barbershop, barber, etc.)
 */

const DB_NAME = 'barbriq-offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  PENDING_BOOKINGS: 'pendingBookings',
  CACHED_BARBERS: 'cachedBarbers',
  CACHED_SERVICES: 'cachedServices',
  USER_PREFERENCES: 'userPreferences',
} as const;

// Interfaces
export interface PendingBooking {
  id: string;
  barbershopId: number;
  barberId: number;
  services: number[];
  date: string;
  time: string;
  notes?: string;
  createdAt: string;
  status: 'pending' | 'syncing' | 'failed';
  retryCount: number;
  userId?: string;
  totalPrice?: number;
}

export interface CachedBarber {
  id: number;
  barbershopId: number;
  name: string;
  tagName?: string;
  bio?: string;
  specialties: string[];
  photo?: string;
  experienceYears: number;
  isActive: boolean;
  cachedAt: string;
}

export interface CachedService {
  id: number;
  barbershopId: number;
  name: string;
  description?: string;
  price: number;
  durationMinutes: number;
  category: string;
  isActive: boolean;
  cachedAt: string;
}

export interface UserPreferences {
  id: string;
  preferredBarbershopId?: number;
  preferredBarberId?: number;
  preferredServices?: number[];
  notificationsEnabled?: boolean;
  theme?: 'light' | 'dark' | 'system';
  lastUpdated: string;
}

// Database instance
let dbInstance: IDBDatabase | null = null;

/**
 * Generate a UUID for pending bookings
 */
export const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Initialize the IndexedDB database
 */
export const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB is not supported in this browser'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;

      // Handle database connection closing
      dbInstance.onclose = () => {
        dbInstance = null;
      };

      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create pendingBookings store
      if (!db.objectStoreNames.contains(STORES.PENDING_BOOKINGS)) {
        const bookingsStore = db.createObjectStore(STORES.PENDING_BOOKINGS, {
          keyPath: 'id',
        });
        bookingsStore.createIndex('status', 'status', { unique: false });
        bookingsStore.createIndex('createdAt', 'createdAt', { unique: false });
        bookingsStore.createIndex('barbershopId', 'barbershopId', { unique: false });
      }

      // Create cachedBarbers store
      if (!db.objectStoreNames.contains(STORES.CACHED_BARBERS)) {
        const barbersStore = db.createObjectStore(STORES.CACHED_BARBERS, {
          keyPath: 'id',
        });
        barbersStore.createIndex('barbershopId', 'barbershopId', { unique: false });
        barbersStore.createIndex('cachedAt', 'cachedAt', { unique: false });
      }

      // Create cachedServices store
      if (!db.objectStoreNames.contains(STORES.CACHED_SERVICES)) {
        const servicesStore = db.createObjectStore(STORES.CACHED_SERVICES, {
          keyPath: 'id',
        });
        servicesStore.createIndex('barbershopId', 'barbershopId', { unique: false });
        servicesStore.createIndex('cachedAt', 'cachedAt', { unique: false });
      }

      // Create userPreferences store
      if (!db.objectStoreNames.contains(STORES.USER_PREFERENCES)) {
        db.createObjectStore(STORES.USER_PREFERENCES, {
          keyPath: 'id',
        });
      }
    };
  });
};

/**
 * Get a transaction for the specified store
 */
const getTransaction = async (
  storeName: string,
  mode: IDBTransactionMode = 'readonly'
): Promise<IDBObjectStore> => {
  const db = await initDB();
  const transaction = db.transaction(storeName, mode);
  return transaction.objectStore(storeName);
};

// =====================
// Pending Bookings
// =====================

/**
 * Save a pending booking to IndexedDB
 */
export const savePendingBooking = async (
  booking: Omit<PendingBooking, 'id' | 'createdAt' | 'status' | 'retryCount'>
): Promise<string> => {
  const store = await getTransaction(STORES.PENDING_BOOKINGS, 'readwrite');

  const pendingBooking: PendingBooking = {
    ...booking,
    id: generateUUID(),
    createdAt: new Date().toISOString(),
    status: 'pending',
    retryCount: 0,
  };

  return new Promise((resolve, reject) => {
    const request = store.add(pendingBooking);
    request.onsuccess = () => resolve(pendingBooking.id);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all pending bookings
 */
export const getPendingBookings = async (): Promise<PendingBooking[]> => {
  const store = await getTransaction(STORES.PENDING_BOOKINGS, 'readonly');

  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get pending bookings by status
 */
export const getPendingBookingsByStatus = async (
  status: PendingBooking['status']
): Promise<PendingBooking[]> => {
  const store = await getTransaction(STORES.PENDING_BOOKINGS, 'readonly');
  const index = store.index('status');

  return new Promise((resolve, reject) => {
    const request = index.getAll(status);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get a single pending booking by ID
 */
export const getPendingBookingById = async (id: string): Promise<PendingBooking | null> => {
  const store = await getTransaction(STORES.PENDING_BOOKINGS, 'readonly');

  return new Promise((resolve, reject) => {
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Remove a pending booking
 */
export const removePendingBooking = async (id: string): Promise<void> => {
  const store = await getTransaction(STORES.PENDING_BOOKINGS, 'readwrite');

  return new Promise((resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Update booking status
 */
export const updateBookingStatus = async (
  id: string,
  status: PendingBooking['status'],
  incrementRetry: boolean = false
): Promise<void> => {
  const booking = await getPendingBookingById(id);
  if (!booking) {
    throw new Error(`Booking with id ${id} not found`);
  }

  const store = await getTransaction(STORES.PENDING_BOOKINGS, 'readwrite');

  const updatedBooking: PendingBooking = {
    ...booking,
    status,
    retryCount: incrementRetry ? booking.retryCount + 1 : booking.retryCount,
  };

  return new Promise((resolve, reject) => {
    const request = store.put(updatedBooking);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get count of pending bookings
 */
export const getPendingBookingsCount = async (): Promise<number> => {
  const store = await getTransaction(STORES.PENDING_BOOKINGS, 'readonly');

  return new Promise((resolve, reject) => {
    const request = store.count();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// =====================
// Cached Barbers
// =====================

/**
 * Cache barbers for a barbershop
 */
export const cacheBarbers = async (
  barbershopId: number,
  barbers: Omit<CachedBarber, 'barbershopId' | 'cachedAt'>[]
): Promise<void> => {
  const store = await getTransaction(STORES.CACHED_BARBERS, 'readwrite');
  const cachedAt = new Date().toISOString();

  // First, clear existing barbers for this barbershop
  const index = store.index('barbershopId');
  const existingRequest = index.getAllKeys(barbershopId);

  await new Promise<void>((resolve, reject) => {
    existingRequest.onsuccess = async () => {
      const keys = existingRequest.result;
      for (const key of keys) {
        store.delete(key);
      }
      resolve();
    };
    existingRequest.onerror = () => reject(existingRequest.error);
  });

  // Add new barbers
  const promises = barbers.map((barber) => {
    return new Promise<void>((resolve, reject) => {
      const cachedBarber: CachedBarber = {
        ...barber,
        barbershopId,
        cachedAt,
      };
      const request = store.add(cachedBarber);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  });

  await Promise.all(promises);
};

/**
 * Get cached barbers for a barbershop
 */
export const getCachedBarbers = async (barbershopId: number): Promise<CachedBarber[]> => {
  const store = await getTransaction(STORES.CACHED_BARBERS, 'readonly');
  const index = store.index('barbershopId');

  return new Promise((resolve, reject) => {
    const request = index.getAll(barbershopId);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get a single cached barber by ID
 */
export const getCachedBarber = async (barberId: number): Promise<CachedBarber | null> => {
  const store = await getTransaction(STORES.CACHED_BARBERS, 'readonly');

  return new Promise((resolve, reject) => {
    const request = store.get(barberId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

// =====================
// Cached Services
// =====================

/**
 * Cache services for a barbershop
 */
export const cacheServices = async (
  barbershopId: number,
  services: Omit<CachedService, 'barbershopId' | 'cachedAt'>[]
): Promise<void> => {
  const store = await getTransaction(STORES.CACHED_SERVICES, 'readwrite');
  const cachedAt = new Date().toISOString();

  // First, clear existing services for this barbershop
  const index = store.index('barbershopId');
  const existingRequest = index.getAllKeys(barbershopId);

  await new Promise<void>((resolve, reject) => {
    existingRequest.onsuccess = async () => {
      const keys = existingRequest.result;
      for (const key of keys) {
        store.delete(key);
      }
      resolve();
    };
    existingRequest.onerror = () => reject(existingRequest.error);
  });

  // Add new services
  const promises = services.map((service) => {
    return new Promise<void>((resolve, reject) => {
      const cachedService: CachedService = {
        ...service,
        barbershopId,
        cachedAt,
      };
      const request = store.add(cachedService);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  });

  await Promise.all(promises);
};

/**
 * Get cached services for a barbershop
 */
export const getCachedServices = async (barbershopId: number): Promise<CachedService[]> => {
  const store = await getTransaction(STORES.CACHED_SERVICES, 'readonly');
  const index = store.index('barbershopId');

  return new Promise((resolve, reject) => {
    const request = index.getAll(barbershopId);
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get a single cached service by ID
 */
export const getCachedService = async (serviceId: number): Promise<CachedService | null> => {
  const store = await getTransaction(STORES.CACHED_SERVICES, 'readonly');

  return new Promise((resolve, reject) => {
    const request = store.get(serviceId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

// =====================
// User Preferences
// =====================

/**
 * Save user preferences
 */
export const saveUserPreferences = async (
  preferences: Omit<UserPreferences, 'lastUpdated'>
): Promise<void> => {
  const store = await getTransaction(STORES.USER_PREFERENCES, 'readwrite');

  const userPreferences: UserPreferences = {
    ...preferences,
    lastUpdated: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const request = store.put(userPreferences);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get user preferences
 */
export const getUserPreferences = async (userId: string): Promise<UserPreferences | null> => {
  const store = await getTransaction(STORES.USER_PREFERENCES, 'readonly');

  return new Promise((resolve, reject) => {
    const request = store.get(userId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

// =====================
// Utility Functions
// =====================

/**
 * Clear all cached data
 */
export const clearAllCache = async (): Promise<void> => {
  const db = await initDB();

  const clearStore = (storeName: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  };

  await Promise.all([
    clearStore(STORES.PENDING_BOOKINGS),
    clearStore(STORES.CACHED_BARBERS),
    clearStore(STORES.CACHED_SERVICES),
    clearStore(STORES.USER_PREFERENCES),
  ]);
};

/**
 * Clear stale cached data (older than specified hours)
 */
export const clearStaleCache = async (maxAgeHours: number = 24): Promise<void> => {
  const cutoffTime = new Date(Date.now() - maxAgeHours * 60 * 60 * 1000).toISOString();

  const clearStaleFromStore = async (storeName: string, timestampField: string): Promise<void> => {
    const store = await getTransaction(storeName, 'readwrite');
    const index = store.index(timestampField);

    return new Promise((resolve, reject) => {
      const range = IDBKeyRange.upperBound(cutoffTime);
      const request = index.openCursor(range);

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  };

  await Promise.all([
    clearStaleFromStore(STORES.CACHED_BARBERS, 'cachedAt'),
    clearStaleFromStore(STORES.CACHED_SERVICES, 'cachedAt'),
  ]);
};

/**
 * Get storage usage information for IndexedDB
 */
export const getStorageInfo = async (): Promise<{
  pendingBookings: number;
  cachedBarbers: number;
  cachedServices: number;
  estimatedUsage?: number;
}> => {
  const [pendingBookings, cachedBarbers, cachedServices] = await Promise.all([
    getPendingBookingsCount(),
    (await getTransaction(STORES.CACHED_BARBERS, 'readonly')).count(),
    (await getTransaction(STORES.CACHED_SERVICES, 'readonly')).count(),
  ]);

  const barberCount = await new Promise<number>((resolve) => {
    (cachedBarbers as unknown as IDBRequest).onsuccess = (e) => {
      resolve((e.target as IDBRequest).result);
    };
  });

  const serviceCount = await new Promise<number>((resolve) => {
    (cachedServices as unknown as IDBRequest).onsuccess = (e) => {
      resolve((e.target as IDBRequest).result);
    };
  });

  // Try to get storage estimate if available
  let estimatedUsage: number | undefined;
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      estimatedUsage = estimate.usage;
    } catch {
      // Storage estimate not available
    }
  }

  return {
    pendingBookings,
    cachedBarbers: barberCount,
    cachedServices: serviceCount,
    estimatedUsage,
  };
};

/**
 * Check if IndexedDB is available
 */
export const isIndexedDBAvailable = (): boolean => {
  return 'indexedDB' in window;
};

/**
 * Close the database connection
 */
export const closeDB = (): void => {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
};

// Export store names for service worker access
export { STORES, DB_NAME, DB_VERSION };
